/**
 * product-form.js
 *
 * Product page interactivity:
 *   - Variant selection (swatches / size buttons / selects) with live
 *     price, availability, gallery, and URL updates.
 *   - Sticky add-to-bag bar driven by IntersectionObserver.
 *   - Thumbnail gallery switching.
 *   - Add-to-cart submission via the shared `window.addToCart` helper.
 *
 * Also includes the shared header scroll behavior used on every page.
 *
 * Depends on `window.addToCart` (cart-drawer.js) being loaded first for
 * add-to-cart submissions. Vanilla ES2017+, no other dependencies.
 */

class ProductForm {
  constructor(form) {
    this.form = form;
    if (!form) return;

    this.variantId       = form.querySelector('[name="id"]');
    this.submitBtn       = form.querySelector('[type="submit"]');
    this.priceEl         = document.getElementById('productPrice');
    this.comparePriceEl  = document.getElementById('productComparePrice');
    this.stickyVariantEl = document.getElementById('stickyVariantTitle');
    this.stickyPriceEl   = document.getElementById('stickyPrice');

    // Default label restored after transient states ("Adding…"/errors).
    this.defaultSubmitLabel = this.submitBtn ? this.submitBtn.textContent.trim() : 'Add to Bag';

    // Parse the variants JSON embedded in the page (Liquid renders this).
    this.variants = this.parseVariants();

    this.bindEvents();
    this.initStickyATB();
    this.initGallery();
  }

  /**
   * Parse the embedded variant data, tolerating a missing or malformed
   * script tag rather than throwing during construction.
   * @returns {Array<object>} Variant records, or an empty array.
   */
  parseVariants() {
    const variantData = document.getElementById('product-variants-json');
    if (!variantData) return [];
    try {
      const parsed = JSON.parse(variantData.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error('Could not parse product variants JSON', err);
      return [];
    }
  }

  bindEvents() {
    // Option selectors (swatches, size buttons, selects).
    this.form.querySelectorAll('[data-option-index]').forEach((input) => {
      input.addEventListener('change', () => this.updateVariant());
    });

    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Guard: addToCart must be available, and a variant must be selected.
      if (typeof window.addToCart !== 'function') {
        console.error('addToCart is unavailable; ensure cart-drawer.js is loaded.');
        return;
      }

      const id = this.variantId ? this.variantId.value : '';
      if (!id) return;

      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Adding…';
      }

      try {
        await window.addToCart({ id: parseInt(id, 10), quantity: 1 });
        // Success: restore the resting label and re-enable.
        this.resetButton();
      } catch (err) {
        // Surface the error briefly on the button, then restore.
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
          this.submitBtn.textContent = err.message || 'Could not add to cart';
        }
        setTimeout(() => this.resetButton(), 2500);
      }
    });
  }

  /**
   * Recompute the selected variant from the current option values and
   * propagate price / availability / gallery / URL updates.
   */
  updateVariant() {
    const selectedOptions = [];

    this.form.querySelectorAll('[data-option-index]').forEach((input) => {
      // Radios/checkboxes only count when checked; selects always count.
      const isSelect = input.tagName === 'SELECT';
      if (input.checked || isSelect) {
        const index = parseInt(input.dataset.optionIndex, 10);
        if (!Number.isNaN(index)) selectedOptions[index] = input.value;
      }
    });

    const variant = this.variants.find((v) =>
      Array.isArray(v.options) && v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (!variant) return;

    if (this.variantId) this.variantId.value = variant.id;
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateGallery(variant);
    this.updateURL(variant);
  }

  /**
   * Update displayed price and (optional) compare-at price.
   * @param {object} variant - The selected variant record.
   */
  updatePrice(variant) {
    const price = this.formatMoney(variant.price);
    if (this.priceEl) this.priceEl.textContent = price;
    if (this.stickyPriceEl) this.stickyPriceEl.textContent = price;

    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePriceEl.hidden = false;
      } else {
        this.comparePriceEl.hidden = true;
      }
    }
  }

  /**
   * Reflect variant availability on the submit button.
   * @param {object} variant - The selected variant record.
   */
  updateAvailability(variant) {
    if (!this.submitBtn) return;
    const available = variant.available;
    this.submitBtn.disabled = !available;
    this.submitBtn.textContent = available ? this.defaultSubmitLabel : 'Sold Out';
  }

  /**
   * Swap the main gallery image to the variant's featured image.
   * @param {object} variant - The selected variant record.
   */
  updateGallery(variant) {
    if (!variant.featured_image) return;
    const mainImg = document.getElementById('productMainImage');
    if (mainImg) mainImg.src = variant.featured_image.src;
  }

  /**
   * Reflect the selected variant in the URL without a navigation, so the
   * page is shareable/refresh-stable. Uses replaceState to avoid polluting
   * browser history with every swatch click.
   * @param {object} variant - The selected variant record.
   */
  updateURL(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /** Restore the submit button to its resting, enabled state. */
  resetButton() {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = false;
    this.submitBtn.textContent = this.defaultSubmitLabel;
  }

  /**
   * Initialize the sticky add-to-bag bar. It becomes visible once the
   * in-page submit button scrolls out of view.
   */
  initStickyATB() {
    const stickyBar = document.getElementById('stickyATB');
    if (!stickyBar || !('IntersectionObserver' in window)) return;

    const atbBtn = this.form.querySelector('[type="submit"]');
    if (!atbBtn) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        stickyBar.classList.toggle('visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });

    observer.observe(atbBtn);
  }

  /** Wire thumbnail clicks to swap the main gallery image. */
  initGallery() {
    const thumbs = document.querySelectorAll('.product-thumb');
    const mainImg = document.getElementById('productMainImage');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (thumb.dataset.full) mainImg.src = thumb.dataset.full;
        thumbs.forEach((t) => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }

  /**
   * Format an integer amount of cents as a currency string.
   * @param {number} cents - Amount in the smallest currency unit.
   * @returns {string} e.g. "$1,299.00".
   */
  formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

/**
 * Header scroll behavior shared across all pages: keeps a spacer in sync
 * with the fixed header height and toggles a `scrolled` class once the
 * page is scrolled past the announcement bar.
 */
(function initHeaderScroll() {
  'use strict';

  const header  = document.getElementById('siteHeader');
  const siteTop = document.getElementById('siteTop');
  const spacer  = document.getElementById('siteTopSpacer');
  if (!header || !siteTop || !spacer) return;

  const announcementBar = document.querySelector('.announcement');
  const announcementH = announcementBar ? announcementBar.offsetHeight : 0;

  /** Match the spacer height to the (possibly fixed) header height. */
  function syncSpacer() {
    spacer.style.height = siteTop.offsetHeight + 'px';
  }

  syncSpacer();
  window.addEventListener('load', syncSpacer);
  window.addEventListener('resize', syncSpacer, { passive: true });

  window.addEventListener('scroll', () => {
    header.classList.toggle('scrolled', window.scrollY > announcementH);
  }, { passive: true });
})();

document.addEventListener('DOMContentLoaded', () => {
  new ProductForm(document.getElementById('product-form'));
});
